import { useState } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import axios from "axios";
import { GoPencil } from "react-icons/go";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Bounce, toast } from "react-toastify";

const ChangePassword = () => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = localStorage.getItem("hiedberg_admin_token");

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/change-password`,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEditMode(false);
      resetForm();
      toast.success("Password updated successfully", {
        position: "top-right",
        autoClose: 5000,
        pauseOnHover: true,
        transition: Bounce,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Password update failed", {
        position: "top-right",
        autoClose: 5000,
        pauseOnHover: true,
        transition: Bounce,
      });
      console.error("Password update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="mt-5 space-y-4 overflow-auto pb-6">
          <div className="rounded-xl border border-gray-300 bg-white p-4 shadow-sm">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, resetForm }) => (
                <Form>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Change Password
                    </h3>

                    {editMode ? (
                      <div className="flex items-center gap-3">
                        <button
                          type="submit"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditMode(false);
                            resetForm();
                          }}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <GoPencil
                        className="hover:text-black h-5 w-5 cursor-pointer"
                        onClick={() => setEditMode(true)}
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-y-4">
                    <TextField
                      label="Current Password"
                      name="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      variant="outlined"
                      size="small"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={values.currentPassword}
                      onChange={handleChange}
                      disabled={!editMode}
                      error={
                        touched.currentPassword &&
                        Boolean(errors.currentPassword)
                      }
                      helperText={
                        touched.currentPassword && errors.currentPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              edge="end"
                              disabled={!editMode}
                            >
                              {showCurrentPassword ? (
                                <AiOutlineEyeInvisible />
                              ) : (
                                <AiOutlineEye />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="New Password"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      variant="outlined"
                      size="small"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={values.newPassword}
                      onChange={handleChange}
                      disabled={!editMode}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                              disabled={!editMode}
                            >
                              {showNewPassword ? (
                                <AiOutlineEyeInvisible />
                              ) : (
                                <AiOutlineEye />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Confirm New Password"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      variant="outlined"
                      size="small"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={values.confirmPassword}
                      onChange={handleChange}
                      disabled={!editMode}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              disabled={!editMode}
                            >
                              {showConfirmPassword ? (
                                <AiOutlineEyeInvisible />
                              ) : (
                                <AiOutlineEye />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangePassword;
